// screens/AudienceWeek.tsx — Chunk AUD-W, plate 06: platform × 7-day grid,
// prev/next week, today marked, cells show the title and "live 19:55".
import { useEffect, useState } from 'preact/hooks';
import { ApiError, getAudienceWeek, type AudienceWeekApi, type AudienceWeekCellApi } from '../lib/api';
import './AudienceWeek.css';

export interface AudienceWeekProps {
  tenantId: string;
  /** ISO date (YYYY-MM-DD), the week's first day. Undefined = this week. */
  start?: string;
  onNavigateWeek: (start: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; week: AudienceWeekApi };

/** Monday of the week containing `d`, as YYYY-MM-DD — the contract's own
 * convention (days: [7 ISO dates]), computed the same way on every call so
 * "this week" never drifts across a render. */
function mondayOf(d: Date): string {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy.toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
}

function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function cellLine(c: AudienceWeekCellApi): string {
  return c.status === 'published' ? `live ${clockLabel(c.at)}` : c.status;
}

export function AudienceWeek({ tenantId, start, onNavigateWeek }: AudienceWeekProps) {
  const resolvedStart = start ?? mondayOf(new Date());
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    getAudienceWeek(tenantId, resolvedStart)
      .then((week) => {
        if (!cancelled) setLoad({ phase: 'ready', week });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the week could not be read' });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, resolvedStart]);

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div class="aud-week">
      <div class="aud-week__nav">
        <button type="button" class="aud-week__nav-btn" onClick={() => onNavigateWeek(addDays(resolvedStart, -7))}>
          ← Prev week
        </button>
        <button type="button" class="aud-week__nav-btn" onClick={() => onNavigateWeek(mondayOf(new Date()))}>
          This week
        </button>
        <button type="button" class="aud-week__nav-btn" onClick={() => onNavigateWeek(addDays(resolvedStart, 7))}>
          Next week →
        </button>
      </div>

      {load.phase === 'loading' ? (
        <div class="aud-week__skeleton" aria-hidden="true" />
      ) : load.phase === 'error' ? (
        <div class="aud-week__say">
          The week could not be read.
          <br />
          <span class="aud-week__say-soft">{load.message}</span>
        </div>
      ) : load.week.rows.length === 0 ? (
        <div class="aud-week__say">
          No connected platforms yet.
          <br />
          <span class="aud-week__say-soft">A connected door's week shows here.</span>
        </div>
      ) : (
        <div class="aud-week__grid-wrap">
          <table class="aud-week__grid">
            <thead>
              <tr>
                <th />
                {load.week.days.map((d) => (
                  <th key={d} class={d === todayIso ? 'is-today' : ''}>
                    {dayLabel(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {load.week.rows.map((row) => (
                <tr key={row.platform}>
                  <th class="aud-week__platform">{row.platform}</th>
                  {row.cells.map((cell, i) => (
                    <td key={i} class={load.week.days[i] === todayIso ? 'is-today' : ''}>
                      {cell.length === 0
                        ? null
                        : cell.map((c, j) => (
                            <div class="aud-week__cell" key={j}>
                              <div class="aud-week__cell-title">{c.title}</div>
                              <div class="aud-week__cell-line">
                                {cellLine(c)}
                                {c.url ? (
                                  <>
                                    {' · '}
                                    <a href={c.url} target="_blank" rel="noreferrer">
                                      open
                                    </a>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
